import { CatalogRoom, RoomMatchResult } from '~/types/stopsell';

const MIN_MATCH_THRESHOLD = 0.5;

function normalize(name: string): string {
  return name.toLowerCase().replace(/\s+/g, ' ').trim();
}

function getBigrams(str: string): Set<string> {
  const bigrams = new Set<string>();
  for (let i = 0; i < str.length - 1; i++) {
    bigrams.add(str.slice(i, i + 2));
  }
  return bigrams;
}

// Dice coefficient: 2 * |intersection| / (|A| + |B|)
// Handles spacing variants well: "hillrock" vs "hill rock" share most bigrams.
export function stringSimilarity(a: string, b: string): number {
  const normA = normalize(a);
  const normB = normalize(b);

  if (normA === normB) return 1;
  if (normA.length < 2 || normB.length < 2) return 0;

  const bigramsA = getBigrams(normA);
  const bigramsB = getBigrams(normB);

  let intersection = 0;
  bigramsA.forEach((bigram) => {
    if (bigramsB.has(bigram)) intersection++;
  });

  return (2 * intersection) / (bigramsA.size + bigramsB.size);
}

function findBestMatch(extractedName: string, catalogRooms: Array<CatalogRoom>): { room: CatalogRoom; score: number } {
  let bestRoom = catalogRooms[0];
  let bestScore = stringSimilarity(extractedName, catalogRooms[0].name);

  for (let i = 1; i < catalogRooms.length; i++) {
    const score = stringSimilarity(extractedName, catalogRooms[i].name);
    if (score > bestScore) {
      bestScore = score;
      bestRoom = catalogRooms[i];
    }
  }

  return { room: bestRoom, score: bestScore };
}

export function matchRoomsToCatalog(
  extractedRooms: Array<{ name: string; roomTypeId: string | null }>,
  catalogRooms: Array<CatalogRoom>,
): Array<RoomMatchResult> {
  return extractedRooms.map(({ name: extractedName, roomTypeId: llmRoomTypeId }) => {
    const noSuggestion: RoomMatchResult = {
      extractedName,
      catalogRoomId: null,
      catalogRoomName: null,
      matchScore: 0,
      isExact: false,
    };

    if (catalogRooms.length === 0) {
      return noSuggestion;
    }

    const { room: bestRoom, score: bestScore } = findBestMatch(extractedName, catalogRooms);

    if (llmRoomTypeId !== null) {
      const llmRoom = catalogRooms.find((r) => r.id === llmRoomTypeId);

      if (!llmRoom || bestRoom.id !== llmRoom.id || bestScore < MIN_MATCH_THRESHOLD) {
        return { ...noSuggestion, matchScore: bestScore };
      }

      return {
        extractedName,
        catalogRoomId: llmRoom.id,
        catalogRoomName: llmRoom.name,
        matchScore: bestScore,
        isExact: bestScore === 1,
      };
    }

    if (bestScore < MIN_MATCH_THRESHOLD) {
      return { ...noSuggestion, matchScore: bestScore };
    }

    return {
      extractedName,
      catalogRoomId: bestRoom.id,
      catalogRoomName: bestRoom.name,
      matchScore: bestScore,
      isExact: bestScore === 1,
    };
  });
}
